/**
 * TanStack Query Client Configuration
 *
 * This file sets up and configures the QueryClient for TanStack Query (React Query).
 * It includes advanced configurations for caching, error handling, retry logic,
 * and performance optimizations.
 *
 * Features:
 * - Custom default query options
 * - Error handling and retry logic
 * - Cache management
 * - Background refetching
 * - Stale time and cache time configuration
 * - Network status handling
 * - Dev tools integration
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Default Query Options
 *
 * These options will be applied to all queries unless overridden.
 * They provide sensible defaults for most use cases.
 */
const defaultQueryOptions = {
    queries: {
        // Time in milliseconds after data is considered stale
        // Stale data will trigger a background refetch when components mount
        staleTime: 2 * 60 * 1000, // 2 minutes - data stays fresh longer

        // Time in milliseconds that unused/inactive cache data remains in memory
        // After this time, data will be garbage collected
        gcTime: 5 * 60 * 1000, // 5 minutes - keep in memory longer

        // Background refetch interval - automatically refresh data
        refetchInterval: 10 * 60 * 1000, // Refetch every 10 minutes to keep data current

        // Number of retry attempts for failed queries
        retry: (failureCount, error) => {
            // Don't retry on 4xx errors (client errors)
            if (error?.response?.status >= 400 && error?.response?.status < 500) {
                return false;
            }
            // Retry up to 3 times for other errors
            return failureCount < 3;
        },

        // Delay between retries (exponential backoff)
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Refetch behavior optimized for performance
        refetchOnWindowFocus: false, // Don't refetch on every window focus
        refetchOnMount: false, // Don't refetch if data is still fresh
        refetchOnReconnect: true, // Refetch when network reconnects
        refetchIntervalInBackground: false, // Only when tab is active

        // Don't suspend on errors (useful for error boundaries)
        suspense: false,

        // Network mode configuration
        networkMode: 'online',
    },
    mutations: {
        // Number of retry attempts for failed mutations
        retry: (failureCount, error) => {
            // Don't retry on 4xx errors (client errors)
            if (error?.response?.status >= 400 && error?.response?.status < 500) {
                return false;
            }
            // Retry up to 2 times for other errors
            return failureCount < 2;
        },

        // Delay between mutation retries
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Network mode for mutations
        networkMode: 'online',
    },
};

/**
 * Create QueryClient Instance
 *
 * This creates the main QueryClient instance with custom configurations.
 * The client manages all query caching, background updates, and state management.
 */
export const queryClient = new QueryClient({
    defaultOptions: defaultQueryOptions,

    /**
     * Logger Configuration
     *
     * Custom logger for development debugging.
     * In production, we might want to integrate with error tracking services.
     */
    logger: {
        log: (...args) => {
            if (process.env.NODE_ENV === 'development') {
                console.log('[QueryClient]', ...args);
            }
        },
        warn: (...args) => {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[QueryClient]', ...args);
            }
        },
        error: (...args) => {
            console.error('[QueryClient]', ...args);
            // In production, you might want to send errors to a monitoring service
            // Example: Sentry.captureException(new Error(args.join(' ')));
        },
    },
});

/**
 * Query Keys Factory
 *
 * This provides a consistent way to generate query keys across the application.
 * Proper query key structure is crucial for cache invalidation and management.
 *
 * Structure: [domain, entity, ...identifiers, ...filters]
 * Examples:
 * - ['users'] - all users
 * - ['users', 'list', { page: 1, limit: 10 }] - paginated users
 * - ['users', 'detail', userId] - specific user
 * - ['posts', 'list', { authorId: 1 }] - posts by author
 */
export const queryKeys = {
    // Base keys for different domains
    users: ['users'],
    posts: ['posts'],
    customers: ['customers'],
    invoices: ['invoices'],
    appointments: ['appointments'],
    inventory: ['inventory'],
    suppliers: ['suppliers'],
    estimates: ['estimates'],
    dashboard: ['dashboard'],
    notifications: ['notifications'],
    settings: ['settings'],

    // User-specific keys
    user: {
        all: () => [...queryKeys.users],
        lists: () => [...queryKeys.users, 'list'],
        list: (filters) => [...queryKeys.users, 'list', filters],
        details: () => [...queryKeys.users, 'detail'],
        detail: (id) => [...queryKeys.users, 'detail', id],
        profile: () => [...queryKeys.users, 'profile'],
    },

    // Post-specific keys (for demo purposes)
    post: {
        all: () => [...queryKeys.posts],
        lists: () => [...queryKeys.posts, 'list'],
        list: (filters) => [...queryKeys.posts, 'list', filters],
        details: () => [...queryKeys.posts, 'detail'],
        detail: (id) => [...queryKeys.posts, 'detail', id],
        comments: (id) => [...queryKeys.posts, 'detail', id, 'comments'],
    },

    // Customer-specific keys
    customer: {
        all: () => [...queryKeys.customers],
        lists: () => [...queryKeys.customers, 'list'],
        list: (filters) => [...queryKeys.customers, 'list', filters],
        details: () => [...queryKeys.customers, 'detail'],
        detail: (id) => [...queryKeys.customers, 'detail', id],
        history: (id) => [...queryKeys.customers, 'detail', id, 'history'],
    },

    // Invoice-specific keys
    invoice: {
        all: () => [...queryKeys.invoices],
        lists: () => [...queryKeys.invoices, 'list'],
        list: (filters) => [...queryKeys.invoices, 'list', filters],
        details: () => [...queryKeys.invoices, 'detail'],
        detail: (id) => [...queryKeys.invoices, 'detail', id],
        stats: () => [...queryKeys.invoices, 'stats'],
    },

    // Dashboard keys
    dashboard: {
        all: () => [...queryKeys.dashboard],
        stats: () => [...queryKeys.dashboard, 'stats'],
        revenueChart: (period) => [...queryKeys.dashboard, 'revenue-chart', period],
        activities: () => [...queryKeys.dashboard, 'recent-activities'],
        appointments: () => [...queryKeys.dashboard, 'upcoming-appointments'],
        pendingInvoices: () => [...queryKeys.dashboard, 'pending-invoices'],
    },
};

/**
 * Cache Invalidation Helpers
 *
 * These functions provide easy ways to invalidate related queries
 * when data changes (typically after mutations).
 */
export const invalidateQueries = {
    // Invalidate all user-related queries
    users: () => queryClient.invalidateQueries({ queryKey: queryKeys.users }),

    // Invalidate specific user
    user: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.user.detail(id) }),

    // Invalidate all post-related queries
    posts: () => queryClient.invalidateQueries({ queryKey: queryKeys.posts }),

    // Invalidate specific post
    post: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.post.detail(id) }),

    // Invalidate customers
    customers: () => queryClient.invalidateQueries({ queryKey: queryKeys.customers }),

    // Invalidate invoices
    invoices: () => queryClient.invalidateQueries({ queryKey: queryKeys.invoices }),

    // Invalidate dashboard data
    dashboard: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard }),
};

/**
 * Prefetch Helpers
 *
 * These functions help prefetch data before it's needed,
 * improving user experience by reducing loading times.
 */
export const prefetchQueries = {
    // Prefetch user data
    user: async (id, queryFn) => {
        await queryClient.prefetchQuery({
            queryKey: queryKeys.user.detail(id),
            queryFn,
            staleTime: 5 * 60 * 1000, // 5 minutes
        });
    },

    // Prefetch posts
    posts: async (filters, queryFn) => {
        await queryClient.prefetchQuery({
            queryKey: queryKeys.post.list(filters),
            queryFn,
            staleTime: 2 * 60 * 1000, // 2 minutes
        });
    },
};

/**
 * Cache Management Helpers
 *
 * These functions help manage the query cache directly.
 */
export const cacheHelpers = {
    // Get cached data
    getCachedData: (queryKey) => queryClient.getQueryData(queryKey),

    // Set cached data
    setCachedData: (queryKey, data) => queryClient.setQueryData(queryKey, data),

    // Remove cached data
    removeCachedData: (queryKey) => queryClient.removeQueries({ queryKey }),

    // Clear all cache
    clearCache: () => queryClient.clear(),

    // Get cache stats (useful for debugging)
    getCacheStats: () => {
        const queryCache = queryClient.getQueryCache();
        return {
            totalQueries: queryCache.getAll().length,
            queries: queryCache.getAll().map((query) => ({
                queryKey: query.queryKey,
                state: query.state.status,
                lastUpdated: query.state.dataUpdatedAt,
                isStale: query.isStale(),
            })),
        };
    },
};

/**
 * Error Handling Helpers
 *
 * These functions help handle errors consistently across the application.
 */
export const errorHandlers = {
    // Default error handler for queries
    defaultQueryError: (error) => {
        console.error('Query Error:', error);

        // You can add global error handling here
        // Example: show toast notification, send to error tracking, etc.

        return error;
    },

    // Default error handler for mutations
    defaultMutationError: (error) => {
        console.error('Mutation Error:', error);

        // You can add global error handling here
        // Example: show toast notification, send to error tracking, etc.

        return error;
    },
};

// Export the configured query client as default
export default queryClient;
